import type { TranslationTree } from '../../types';

export const toolRecommender: TranslationTree = {
  toolRecommender: {
    page: {
      breadcrumb: 'Tool Eco-Design',
      badge: 'Genetic Algorithm Optimiser',
      title: 'Find the best-fit tool for your machining context',
      description:
        'Configure your machining constraints and run the genetic algorithm. The model evaluates tool candidates and returns them ranked by predicted performance.',
    },
    tabs: {
      generate: 'Generate',
      history: 'History',
    },
    form: {
      sectionTitle: 'Configuration',
      descriptionLabel: 'Run description (optional)',
      descriptionPlaceholder: 'e.g. Aluminium finishing pass',
      materialsLabel: 'Materials',
      operationsLabel: 'Machining operations',
      nJobsLabel: 'Parallel threads (nJobs)',
      topNLabel: 'Results (topN)',
      baseToolLabel: 'Base tool (optional)',
      baseToolPlaceholder: '{"d1": 1, "Nb dents": 3, ...}',
      runButton: 'Run GA',
    },
    running: {
      title: 'GA running…',
      hint: 'This may take several minutes (up to 5). Please wait.',
      resumedHint: 'You left and came back — the experiment is still running in the background.',
    },
    results: {
      redirect: 'Experiment complete — opening results…',
    },
    timeout: {
      title: 'GA still running',
      message:
        'The algorithm exceeded the response timeout. Results will appear in History once complete.',
      historyHint: 'Switch to the History tab and refresh to see the result when it is ready.',
    },
    idle: {
      title: 'No results yet',
      description: 'Configure constraints and launch the GA to see ranked tool candidates.',
    },
    error: {
      title: 'Inference failed',
    },
    history: {
      sectionTitle: 'Past experiments',
      empty: 'No experiments yet. Run the GA to see results here.',
      colDate: 'Date',
      colDescription: 'Description',
      colId: 'Experiment ID',
      colResults: 'Results',
      viewLink: 'View',
      refresh: 'Refresh history',
      results: '{{n}} results',
    },
    detail: {
      breadcrumb: 'Experiment',
      noResults: 'No results stored for this experiment.',
      resultLabel: 'Result',
      baseScoreLabel: 'Base tool score',
      generatedScoreLabel: 'Generated tool score',
      baseFromCatalog: 'Base tool: catalog article',
      baseFromUser: 'Base tool: your tool',
      noBaseTool: 'No base tool available for comparison.',
      colField: 'Field',
      colBase: 'Base tool',
      colGenerated: 'Generated tool',
      advancedTitle: 'Advanced — {{n}} more base tool fields',
      exportXlsx: 'Export to Excel',
      exportRank: 'Rank',
      exportMaterial: 'Material',
      exportOperation: 'Operation',
      exportBaseToolArticle: 'Base tool article',
    },
    baseTool: {
      sheetTitle: 'Base tool',
      sheetDescription:
        'Describe the tool to optimise. The form starts from the most common tool of the catalogue; every field is required, and the JSON tab lets you paste an existing tool.',
      tabForm: 'Form',
      tabJson: 'JSON',
      filled: '{{filled}} / {{total}} fields filled',
      errors: '{{n}} invalid value(s)',
      choose: 'Choose…',
      range: '{{min}} to {{max}} {{unit}}',
      rangeExclusive: '> {{min}} to {{max}} {{unit}}',
      clear: 'Clear all',
      restoreDefaults: 'Default values',
      apply: 'Use this tool',
      jsonLabel: 'Tool JSON — keys are the ISO 13399 aliases',
      groups: {
        dimensions: 'Dimensions',
        cutting: 'Cutting',
        angles: 'Angles and clearances',
        grinding: 'Grinding, core and flutes',
        material: 'Tool material',
      },
      issues: {
        outOfRange: 'Out of range: {{range}}',
        notANumber: 'Number expected',
        unknownChoice: 'Unrecognised value',
      },
      json: {
        syntax: 'Invalid JSON: {{message}}',
        notAnObject: 'A JSON object is expected, for example {"d1": 3}',
        unknownKeys: 'Keys unknown to the model, they will be ignored: {{keys}}',
      },
      summary: {
        none: 'No base tool',
        noneHint: 'The algorithm will start from the best catalogue tools.',
        line: 'Ø{{d1}} · {{teeth}} teeth · helix {{helix}}° · {{substrate}}',
        setHint: 'Used as the starting point of the algorithm.',
        define: 'Define a tool…',
        edit: 'Edit…',
        remove: 'Remove',
      },
    },
    fields: {
      d1: 'Cutting diameter (d1)',
      l1: 'Cutting length (l1)',
      nbDents: 'Number of teeth',
      helice1: 'Helix angle 1 [°]',
      helice2: 'Helix angle 2 [°]',
      sensDeCoupe: 'Cutting direction',
      profil: 'Profile',
      pasDuProfil: 'Profile pitch',
      formeFraise: 'Cutter shape',
      coin: 'Corner',
      valeurCoin: 'Corner value',
      coupeCentre: 'Centre cutting',
      casseCoupe: 'Edge chamfer',
      largeurCasseCoupe: 'Edge chamfer width',
      angleCoupeBout: 'End rake angle [°]',
      angleCoupeDiametreMin: 'Min. peripheral rake angle (flute 1) [°]',
      angleCoupeDiametreMax: 'Max. peripheral rake angle (flute 1) [°]',
      depouilleBout1: '1st end clearance [°]',
      depouilleBout2: '2nd end clearance [°]',
      depouilleDiam1: '1st peripheral clearance [°]',
      depouilleDiam2: '2nd peripheral clearance [°]',
      depouilleDiamType: 'Peripheral clearance type',
      affutageBout: 'End grind',
      largeurAffutageBout: 'End grind width',
      depassementCentre: 'Centre overlap',
      largeurListel: 'Margin width',
      largeurAme: 'Web width',
      noyauMm: 'Core [mm]',
      noyauArriere: 'Back core',
      creux1: 'Relief 1 [°]',
      creux2: 'Relief 2 [°]',
      creux3: 'Relief 3 [°]',
      longueurCreux: 'Relief length',
      indexTaillage: 'Fluting indexing',
      indexTaillageAngle: 'Fluting indexing angle',
      rayonRectifiage: 'Grinding radius',
      longueurUtileMax: 'Maximum usable length LUX [mm]',
      demiAngleCone: 'Body cone half-angle BHTA [°]',
      diamCollet: 'Neck diameter #1 (D2)',
      longCollet: 'Neck length #1 (L2)',
      fabricationType: 'Manufacturing type',
      substrat: 'Substrate / tool material',
      percentCo: 'Cobalt content [%]',
      tailleGrain: 'Grain size',
      reference: 'Reference',
      article: 'Article',
    },
  },
};
